'use client'

import { MouseEvent } from 'react'
import { Icon, Image } from '@vinted/web-ui'
import { PickupPointBackground32, PickupPointBackground48 } from '@vinted/monochrome-icons'
import classNames from 'classnames'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  DropOffPointModel,
  clickEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useDropOffPointMapContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import DropOffPointInfoWindow from './DropOffPointInfoWindow'
import HoverDelay from '../../../HoverDelay'

type Props = {
  lat: number
  lng: number
  dropOffPoint: DropOffPointModel
  isActive: boolean
  isMouseOverListElement: boolean
  onClick: (dropOffPoint: DropOffPointModel) => void
  id: number
}

const DropOffPointMarker = ({
  // google-map-react is using lat and lng internally
  lat /* eslint-disable-line @typescript-eslint/no-unused-vars */,
  lng /* eslint-disable-line @typescript-eslint/no-unused-vars */,
  dropOffPoint,
  isActive,
  isMouseOverListElement,
  onClick,
  id,
}: Props) => {
  const breakpoints = useBreakpoint()
  const { track } = useTracking()
  const { dropOffPointSource } = useDropOffPointMapContext()

  const transaction = useConversationContext().conversation?.transaction

  const transactionId = transaction?.id
  const shipmentStatus = transaction?.shipment?.status

  const trackMarkerClick = (nearbyDropOffPoint: DropOffPointModel) => {
    const event = clickEvent({
      screen: 'seller_drop_off_map',
      target: 'drop_off_point',
      targetDetails: JSON.stringify({
        transaction_id: transactionId,
        drop_off_point_code: nearbyDropOffPoint.code,
        distance_to_address: nearbyDropOffPoint.distance,
        distance_unit: nearbyDropOffPoint.distanceUnit,
        shipment_status: shipmentStatus,
        drop_off_points_source: dropOffPointSource,
      }),
    })

    track(event)
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation()

    trackMarkerClick(dropOffPoint)

    onClick(dropOffPoint)
  }

  const renderMarkerIcon = () => (
    <div className="drop-off-point-map__locker-icon">
      <Image
        src={dropOffPoint.pointTypeIconUrl}
        fallbackSrc=""
        size={isActive ? 'regular' : 'small'}
        alt=""
      />
    </div>
  )

  const renderMarker = (isOverMarker: boolean) => {
    const isMouseOverMarker = isOverMarker || isMouseOverListElement

    const markerClass = classNames('drop-off-point-map__location-pin', {
      'drop-off-point-map__location-pin--hovered': isMouseOverMarker,
    })
    const backroundClass = classNames('drop-off-point-map__location-pin-background', {
      'drop-off-point-map__location-pin-background--active': isActive,
    })

    return (
      <button
        type="button"
        className={markerClass}
        onClick={handleClick}
        data-testid="drop-off-point-location-pin"
      >
        <div className={backroundClass} data-testid="drop-off-point-background">
          <Icon
            name={isActive ? PickupPointBackground48 : PickupPointBackground32}
            color="primary-default"
            aria={{ 'aria-hidden': 'true' }}
            testId={`carrier-icon-${id}`}
          />
        </div>
        {renderMarkerIcon()}
      </button>
    )
  }

  return (
    <HoverDelay>
      {({ isMouseOverElement }) => {
        const { desktops } = breakpoints
        const isMouseOverMarker = !!desktops && isMouseOverElement

        return (
          <div className="u-position-relative">
            <DropOffPointInfoWindow
              dropOffPoint={dropOffPoint}
              show={isMouseOverMarker}
              onClick={handleClick}
              id={id}
              isActive={isActive}
            />
            {renderMarker(isMouseOverMarker)}
          </div>
        )
      }}
    </HoverDelay>
  )
}

export default DropOffPointMarker
